import React, { useState } from "react";
import {
  Button,
  Card,
  FormGroup,
  HTMLSelect,
  HTMLTable,
  InputGroup,
  Intent,
  NonIdealState,
  Tooltip,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useLoaderData, Navigate } from "react-router-dom";
import { createUser, removeUser, updateUserRole, UserInfo, UserRole } from "../api/cppmodelApi";
import { getNormalizedAuthClaims } from "../auth/authClaims";

type CreateRoleValue = UserRole;
type UserAction = "promote" | "demote" | "remove";

const roleOptions: { label: string; value: CreateRoleValue }[] = [
  { label: "Workspace Admin", value: "admin" },
  { label: "Workspace User", value: "member" },
];

function getDisplayName(user: UserInfo): string {
  if (user.name && user.name.trim().length > 0) {
    return user.name;
  }

  const joinedName = [user.firstName, user.lastName]
    .filter((part): part is string => Boolean(part && part.trim().length > 0))
    .join(" ");

  if (joinedName.length > 0) {
    return joinedName;
  }

  if (user.username && user.username.trim().length > 0) {
    return user.username;
  }

  return user.email;
}

export const UserAdministration: React.FC = () => {
  const { isWorkspaceAdmin } = getNormalizedAuthClaims();
  const loadedUsers = (useLoaderData() as UserInfo[] | null) ?? [];
  const [users, setUsers] = useState<UserInfo[]>(loadedUsers);
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState<CreateRoleValue>("member");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeUserAction, setActiveUserAction] = useState<{ userId: string; action: UserAction } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isWorkspaceAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleAddUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage("Enter an email address before adding a user.");
      return;
    }

    setIsSubmitting(true);

    const createdUser = await createUser(trimmedEmail, selectedRole);

    if (!createdUser) {
      setErrorMessage("Could not add the user. Please try again.");
      setIsSubmitting(false);
      return;
    }

    setUsers((prevUsers) => [...prevUsers, createdUser]);
    setEmail("");
    setSelectedRole("member");
    setIsSubmitting(false);
  };

  const handleRemoveUser = async (userId: string) => {
    setErrorMessage(null);
    setActiveUserAction({ userId, action: "remove" });

    const removed = await removeUser(userId);

    if (!removed) {
      setErrorMessage("Could not remove the user. Please try again.");
      setActiveUserAction(null);
      return;
    }

    setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
    setActiveUserAction(null);
  };

  const handleUpdateUserRole = async (
    userId: string,
    role: UserRole,
    action: Extract<UserAction, "promote" | "demote">,
    errorLabel: string
  ) => {
    setErrorMessage(null);
    setActiveUserAction({ userId, action });

    const updatedUser = await updateUserRole(userId, role);

    if (!updatedUser) {
      setErrorMessage(`Could not ${errorLabel} the user. Please try again.`);
      setActiveUserAction(null);
      return;
    }

    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === userId ? updatedUser : user))
    );
    setActiveUserAction(null);
  };

  const handlePromoteUser = async (user: UserInfo) => {
    await handleUpdateUserRole(user.id, "admin", "promote", "promote");
  };

  const handleDemoteUser = async (user: UserInfo) => {
    await handleUpdateUserRole(user.id, "member", "demote", "demote");
  };

  return (
    <div className="cm-page" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <h1 className="cm-page-title">User Administration</h1>
        <p className="cm-page-subtitle">Invite people to this workspace and manage their roles.</p>
      </div>

      <Card style={{ maxWidth: "560px" }}>
        <form onSubmit={handleAddUser}>
          <FormGroup label="Add User" helperText="Invite a user to the current workspace.">
            {errorMessage && (
              <div className="cm-error-text" style={{ marginBottom: "10px" }}>
                {errorMessage}
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
              <InputGroup
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="user@example.com"
                type="email"
                style={{ minWidth: "260px", flex: 1 }}
              />
              <HTMLSelect
                value={selectedRole}
                onChange={(event) => setSelectedRole(event.target.value as CreateRoleValue)}
                options={roleOptions.map((role) => ({ label: role.label, value: role.value }))}
              />
              <Button intent={Intent.PRIMARY} type="submit" loading={isSubmitting}>
                Add User
              </Button>
            </div>
          </FormGroup>
        </form>
      </Card>

      <Card>
        {users.length === 0 ? (
          <NonIdealState
            title="No users found"
            description="There are no users in this workspace yet."
          />
        ) : (
          <HTMLTable striped interactive style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th style={{ width: "1%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{getDisplayName(user)}</td>
                  <td>{user.email}</td>
                  <td>{user.role ?? ""}</td>
                  <td>
                    <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end", flexWrap: "nowrap" }}>
                      <Tooltip content="Promote user">
                        <Button
                          small
                          minimal
                          icon={IconNames.ArrowUp}
                          disabled={activeUserAction?.userId === user.id || user.role === "admin"}
                          loading={activeUserAction?.userId === user.id && activeUserAction.action === "promote"}
                          onClick={() => handlePromoteUser(user)}
                        />
                      </Tooltip>
                      <Tooltip content="Demote user">
                        <Button
                          small
                          minimal
                          icon={IconNames.ArrowDown}
                          disabled={activeUserAction?.userId === user.id || user.role === "member"}
                          loading={activeUserAction?.userId === user.id && activeUserAction.action === "demote"}
                          onClick={() => handleDemoteUser(user)}
                        />
                      </Tooltip>
                      <Tooltip content="Remove user">
                        <Button
                          small
                          minimal
                          intent={Intent.DANGER}
                          icon={IconNames.Trash}
                          disabled={activeUserAction?.userId === user.id}
                          loading={activeUserAction?.userId === user.id && activeUserAction.action === "remove"}
                          onClick={() => handleRemoveUser(user.id)}
                        />
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        )}
      </Card>
    </div>
  );
};
